import './Details.scss';

import { useFormikContext } from 'formik';
import React from 'react';

import type { AdTypeV5 } from '../../../apis/graphql';
import type { AdType, Config } from '../../../configs';
import { convertAdType } from '../../../configs';
import bem from '../../../utils/bem';
import type { DropdownOption } from '../../Dropdown';
import Dropdown from '../../Dropdown';
import { Switch, TextField } from '../../Form';
import { useFieldFast } from '../../Form/hooks';
import type { AdFormValues } from '../adFormik';
import { getInitialCreativeBuilderValues } from '../adFormik';
import { AdFormFields } from '../adFormik/fields';
import type { CreativesBuilderValues } from '../CreativeBuilders/types';

export interface DetailsProps {
  permittedAdTypes: Config<AdType>[];
  isDeliveryStatusDisabled: boolean;
  readonly?: boolean;
}

const [block, element] = bem('details');

const Details = ({ permittedAdTypes, isDeliveryStatusDisabled, readonly }: DetailsProps): JSX.Element => {
  const [{ value: adType }, , { setValue: setAdType }] = useFieldFast<string>('type');
  const [, , { setValue: setCreative }] = useFieldFast<CreativesBuilderValues>('creative');
  const [{ value: id }] = useFieldFast<string>('id');
  const { isSubmitting, values } = useFormikContext<AdFormValues>();
  const isExistingAd = !!id;
  const adTypeDisplayName = convertAdType(adType).displayName;

  const mapUserAdTypeDataToDropdownOptions = (permittedTypes: Config<AdType>[]): DropdownOption[] => {
    return permittedTypes.map(({ key, displayName }) => ({ label: `${displayName}`, value: key })) || [];
  };

  const onAdTypeChange = (newAdType: DropdownOption | null): void => {
    setCreative(
      getInitialCreativeBuilderValues(
        'selectOption',
        newAdType?.value as AdTypeV5,
        values.brandName,
        values.creative.creativeLibraryId
      )
    );

    setAdType(newAdType?.value as string);
  };

  return (
    <div className={block()}>
      <TextField
        id="name"
        name="name"
        formFieldName={AdFormFields.Name}
        className={element('ad-name-input')}
        label="Ad Name"
        secondaryLabel=" (Required)"
        disabled={readonly}
      />
      <div className={element('type-rating-wrapper')}>
        <Dropdown
          id="type"
          value={(adTypeDisplayName as unknown) as DropdownOption | null}
          emptyDisplayText={adTypeDisplayName}
          label="Ad Type (Required)"
          name="type"
          options={mapUserAdTypeDataToDropdownOptions(permittedAdTypes)}
          isSearchable={false}
          isDisabled={isSubmitting || isExistingAd || readonly}
          onChange={onAdTypeChange}
          dataTestId="ad-type"
        />
      </div>
      <Switch
        id="deliveryStatus"
        formFieldName="deliveryStatus"
        className={element('delivery-switch')}
        colorWhenOn={'black'}
        disabled={isDeliveryStatusDisabled || readonly}
        label="Delivery Status"
        info={(value): string =>
          value ? 'Your ad is live!' : 'Your ad cannot go live until it has been approved in ATC'
        }
      />
    </div>
  );
};

export default Details;
